import { Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import TeacherShell, { TeacherPageHeader } from '@/components/layout/TeacherShell'
import { Card, CardContent, Button } from '@/components/ui'

const BAND_ORDER = ['5k', '6k', '7k', '8k', '9k', '10k', '11k', '12k', '13k', '14k']

export default function Show({ assessment, administrations }) {
  const { t } = useTranslation()
  const sortedBands = BAND_ORDER.filter(b => assessment.item_count_by_band?.[b])

  return (
    <TeacherShell scrollTitle={
      <span>
        <Link href="/teacher/assessments" className="hover:underline">{t('teacherAssessments.title')}</Link>
        {' > '}{assessment.title}
      </span>
    }>
      <TeacherPageHeader title={assessment.title} />

      <div className="grid gap-6 lg:grid-cols-3 mb-8">
        {/* Info card */}
        <Card className="lg:col-span-2">
          <CardContent className="p-5">
            <p className="text-gray-600 dark:text-gray-300 mb-4">{assessment.description}</p>

            <div className="flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
              <span>{assessment.item_count} {t('teacherAssessments.totalItems')}</span>
              <span>{assessment.administration_count} {t('teacherAssessments.completions')}</span>
              {assessment.estimated_duration_minutes && (
                <span>~{assessment.estimated_duration_minutes} min</span>
              )}
              <span className={`font-medium ${assessment.status === 'ready' ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                {assessment.status}
              </span>
            </div>

            {assessment.status === 'ready' && (
              <div className="mt-4">
                <Link href={`/teacher/assessments/${assessment.id}/trial`} method="post" as="button">
                  <Button>{t('teacherAssessments.tryItPreview')}</Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Band distribution */}
        {sortedBands.length > 0 && (
          <Card>
            <CardContent className="p-5">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('teacherAssessments.itemBankByBand')}</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 dark:text-gray-400">
                    <th className="pb-1 font-medium">{t('teacherAssessments.band')}</th>
                    <th className="pb-1 font-medium text-right">{t('teacherAssessments.items')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBands.map(band => (
                    <tr key={band} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1 text-gray-700 dark:text-gray-300">{band}</td>
                      <td className="py-1 text-right text-gray-600 dark:text-gray-400">
                        {assessment.item_count_by_band[band]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Student results */}
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('teacherAssessments.studentResults')}</h2>

      {administrations.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-sm">{t('teacherAssessments.noStudentResults')}</p>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                    <th className="px-4 py-3 font-medium">{t('common.student')}</th>
                    <th className="px-4 py-3 font-medium">{t('teacherAssessments.vocabSize')}</th>
                    <th className="px-4 py-3 font-medium">{t('teacherAssessments.gradeLevel')}</th>
                    <th className="px-4 py-3 font-medium">{t('teacherAssessments.duration')}</th>
                    <th className="px-4 py-3 font-medium">{t('teacherAssessments.date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {administrations.map(a => (
                    <tr key={a.id} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="px-4 py-3 text-gray-900 dark:text-white">{a.student_name}</td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {a.summary?.estimated_vocab_size?.toLocaleString() || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300">
                        {a.summary?.estimated_grade_level || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{a.duration || '—'}</td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">
                        {a.completed_at ? new Date(a.completed_at).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </TeacherShell>
  )
}
